import { Button } from "./ui/button"
import { Menu } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigation } from "@/hooks/use-navigation";
import { JoinTeamModal } from "./JoinTeamModal";

export const Navbar = () => {
  const navigate = useNavigation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-[49] bg-white/80 backdrop-blur-sm border-b">
        <div className="container mx-auto flex items-center justify-between py-4 px-4">
          {/* Left side: Logo */}
          <div
            className="cursor-pointer"
            onClick={() => handleNavigation('/')}
          >
            <img
              src="/navbar-logo.png"
              alt="Kampüs Logo"
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <span
              onClick={() => handleNavigation('/')}
              className="text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
            >
              Ana Sayfa
            </span>
            <span
              onClick={() => handleNavigation('/coaches')}
              className="text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
            >
              Mentörlerimiz
            </span>
            <span
              onClick={() => handleNavigation('/puan-hesaplama')}
              className="text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
            >
              Puan Hesaplama
            </span>
            <span
              onClick={() => handleNavigation('/blog')}
              className="text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
            >
              Blog
            </span>
            <span
              onClick={() => handleNavigation('/contact')}
              className="text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
            >
              İletişim
            </span>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">

            <JoinTeamModal />

            <Button
              variant="outline"
              onClick={() => handleNavigation('/koc-oner')}
              className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white"
            >
              Ücretsiz Görüşme
            </Button>
            <Button
              onClick={() => handleNavigation('/coaches')}
              className="group relative bg-brand-primary hover:bg-brand-primary/90 text-white overflow-hidden"
            >
              Mentörlük Al
            </Button>
          </div>

          {/* Mobile Menu and CTA */}
          <div className="md:hidden flex items-center gap-3">
            <Button
              onClick={() => handleNavigation('/coaches')}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white text-sm px-3 py-2 h-9"
            >
              Mentörlük Al
            </Button>
            <button
              onClick={toggleMobileMenu}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu className="h-6 w-6 text-brand-primary/70" />
            </button>
          </div>
        </div>

        {/* Animated Banner Strip */}
        <div className="bg-brand-primary overflow-hidden whitespace-nowrap relative" onClick={() => handleNavigation('/coaches')}>
          <div className="inline-block animate-infinite-scroll">
            <span className="inline-block py-1 text-white px-4">
              🎯 Hemen Ücretsiz Dene!
            </span>
            <span className="inline-block py-1 text-white px-4">
              🎉 YKS'ye Kadar Paketlerde %15'e Varan İndirim!
            </span>
            <span className="inline-block py-1 text-white px-4">
              ✨ Memnun Kalmazsan İlk 7 Gün İçerisinde Paran İade
            </span>
            <span className="inline-block py-1 text-white px-4">
              🎯 Hemen Ücretsiz Dene!
            </span>
            <span className="inline-block py-1 text-white px-4">
              🎉 YKS'ye Kadar Paketlerde %15'e Varan İndirim!
            </span>
            <span className="inline-block py-1 text-white px-4">
              ✨ Memnun Kalmazsan İlk 7 Gün İçerisinde Paran İade
            </span>
          </div>
          <div className="inline-block animate-infinite-scroll">
            <span className="inline-block py-1 text-white px-4">
              🎯 Hemen Ücretsiz Dene!
            </span>
            <span className="inline-block py-1 text-white px-4">
              🎉 YKS'ye Kadar Paketlerde %15'e Varan İndirim!
            </span>
            <span className="inline-block py-1 text-white px-4">
              ✨ Memnun Kalmazsan İlk 7 Gün İçerisinde Paran İade
            </span>
            <span className="inline-block py-1 text-white px-4">
              🎯 Hemen Ücretsiz Dene!
            </span>
            <span className="inline-block py-1 text-white px-4">
              🎉 YKS'ye Kadar Paketlerde %15'e Varan İndirim!
            </span>
            <span className="inline-block py-1 text-white px-4">
              ✨ Memnun Kalmazsan İlk 7 Gün İçerisinde Paran İade
            </span>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-[998] md:hidden"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="fixed right-0 top-0 bottom-0 w-[280px] bg-white shadow-xl z-[999] md:hidden"
            >
              <div className="flex flex-col h-full">
                <div className="p-6 space-y-6">
                  {/* Navigation Links */}
                  <div className="space-y-4">
                    <div
                      onClick={() => handleNavigation('/')}
                      className="text-lg font-medium text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
                    >
                      Ana Sayfa
                    </div>
                    <div
                      onClick={() => handleNavigation('/coaches')}
                      className="text-lg font-medium text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
                    >
                      Mentörlerimiz
                    </div>
                    <div
                      onClick={() => handleNavigation('/puan-hesaplama')}
                      className="text-lg font-medium text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
                    >
                      Puan Hesaplama
                    </div>
                    <div
                      onClick={() => handleNavigation('/blog')}
                      className="text-lg font-medium text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
                    >
                      Blog
                    </div>
                    <div
                      onClick={() => handleNavigation('/contact')}
                      className="text-lg font-medium text-text-primary hover:text-brand-primary transition-colors cursor-pointer"
                    >
                      İletişim
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3 pt-6 border-t">

                    <JoinTeamModal isMobile={true} />

                    <Button
                      variant="outline"
                      onClick={() => handleNavigation('/koc-oner')}
                      className="w-full border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white"
                    >
                      Ücretsiz Görüşme
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/coaches')}
                      className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white"
                    >
                      Mentörlük Al
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;